"use client";

import * as React from "react";
import {
  forwardRef,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import TextField from "@/components/textField";
import Autocomplete from "@mui/material/Autocomplete";
// Precomputed from country-codes-list (unique calling codes, first country wins),
// so the ~80 KB library isn't shipped to the browser.
import countries from "@/data/countryCallingCodes.json";
import dynamic from "next/dynamic";

const ReactCountryFlag = dynamic(() => import("react-country-flag"), {
  ssr: true,
  loading: () => <div className="size-4 bg-zinc-200" />,
});

const defaultValues = {
  mobileCountryCode: "971",
  mobileNumber: "",
};

const MobilePhoneSelect = forwardRef(function ({ onChange, errors }, ref) {
  const id = useId();

  const [values, setValues] = useState(defaultValues);

  const hasError = !!(errors?.mobileNumber || errors?.mobileCountryCode);

  const handleFieldChange = useCallback(
    (field) => {
      const newValues = { ...values, ...field };
      if (onChange) onChange(newValues);
      setValues(newValues);
    },
    [values, onChange],
  );

  const handleNumber = useCallback(
    (length) => (event) => {
      const { value } = event.target;
      const matched = value?.match(/\d*/g)?.join("");
      const allowedValue = matched?.slice(0, length);

      handleFieldChange({ mobileNumber: allowedValue });
    },
    [handleFieldChange],
  );

  useImperativeHandle(ref, () => ({
    reset: () => {
      setValues(defaultValues);
    },
  }));

  const countryCode = useMemo(
    () =>
      countries.find(
        (country) => country.countryCallingCode === values?.mobileCountryCode
      ),
    [values?.mobileCountryCode],
  );

  return (
    <div>
      <div className="flex items-center gap-2">
        <Autocomplete
          id={id}
          className="w-[110px] sm:w-[160px] lg:w-[110px]"
          value={countryCode}
          options={countries}
          autoHighlight
          getOptionLabel={(option) => `+ ${option.countryCallingCode}`}
          onChange={(event, newValue) => {
            handleFieldChange({
              mobileCountryCode: newValue?.countryCallingCode,
            });
          }}
          disableClearable
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li
                key={key}
                {...optionProps}
                className="flex items-center gap-2 border-b py-1 px-2.5 hover:bg-black/10 cursor-pointer"
              >
                <ReactCountryFlag countryCode={option.countryCode} svg />
                <span className="whitespace-nowrap">
                  + {option.countryCallingCode}
                </span>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country Code"
              error={hasError}
              inputProps={{
                ...params.inputProps,
                autoComplete: "nope", // disable autocomplete and autofill
              }}
            />
          )}
        />
        <TextField
          className="flex-1 min-w-24"
          placeholder="Mobile number"
          label="Mobile number"
          inputProps={{ value: values?.mobileNumber }}
          onChange={handleNumber(12)}
          error={hasError}
        />
      </div>
      {hasError && (
        <p className="text-red-500 text-xs ml-2.5 mt-1.5 flex flex-col">
          <span>{errors?.mobileCountryCode}</span>
          <span>{errors?.mobileNumber}</span>
        </p>
      )}
    </div>
  );
});

MobilePhoneSelect.displayName = "MobilePhoneSelect";

export default MobilePhoneSelect;
